import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PlanProvider } from "./contexts/PlanContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import { Toaster } from "./components/ui/sonner";

// Layout
import { DashboardLayout } from "./components/layout/DashboardLayout";

// Auth Pages
import { LoginPage } from "./pages/auth/LoginPage";
import { AuthCallback } from "./pages/auth/AuthCallback";
import { ForceChangePasswordPage } from "./pages/auth/ForceChangePasswordPage";

// Main Pages
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { ClientsPage } from "./pages/clients/ClientsPage";
import { ClientDetailPage } from "./pages/clients/ClientDetailPage";
import { ProjectsPage } from "./pages/projects/ProjectsPage";
import { ProjectDetailPage } from "./pages/projects/ProjectDetailPage";
import { QuotesPage } from "./pages/finance/QuotesPage";
import { InvoicesPage } from "./pages/finance/InvoicesPage";
import { TeamPage } from "./pages/team/TeamPage";
import { TimeTrackingPage } from "./pages/time/TimeTrackingPage";
import { AIAssistantPage } from "./pages/ai/AIAssistantPage";
import { NotificationsPage } from "./pages/notifications/NotificationsPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { BillingSettingsPage } from "./pages/settings/BillingSettingsPage";
import { QuoteSettingsPage } from "./pages/settings/QuoteSettingsPage";
import { SubscriptionPage } from "./pages/settings/SubscriptionPage";
import { SubscriptionSuccessPage } from "./pages/settings/SubscriptionSuccessPage";
import UpgradePage from "./pages/settings/UpgradePage";
import { FeatureGate } from "./components/layout/FeatureGate";
import { CalendarPage } from "./pages/calendar/CalendarPage";
import { DocumentsPage } from "./pages/documents/DocumentsPage";
import { UsersPage } from "./pages/users/UsersPage";
import { MessagingPage } from "./pages/messaging/MessagingPage";
import { AcceptInvitePage } from "./pages/auth/AcceptInvitePage";

// Portal Pages
import { PortalLayout } from "./components/portal/PortalLayout";
import { PortalAuthPage } from "./pages/portal/PortalAuthPage";
import { PortalDashboardPage } from "./pages/portal/PortalDashboardPage";
import { PortalProjectsPage } from "./pages/portal/PortalProjectsPage";
import { PortalProjectDetailPage } from "./pages/portal/PortalProjectDetailPage";
import { PortalQuotesPage } from "./pages/portal/PortalQuotesPage";
import { PortalInvoicesPage } from "./pages/portal/PortalInvoicesPage";

// Admin Pages
import { SuperAdminLayout } from "./components/admin/SuperAdminLayout";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminTenantsPage } from "./pages/admin/AdminTenantsPage";
import { AdminSettingsPage } from "./pages/admin/AdminSettingsPage";
import { AdminSubscriptionsPage } from "./pages/admin/AdminSubscriptionsPage";
import { AdminPlansPage } from "./pages/admin/AdminPlansPage";
import { AdminAuditPage } from "./pages/admin/AdminAuditPage";

import "./index.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, user, isPortalClient } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Force password change if required (but allow the change-password page itself)
    if (user?.must_change_password && location.pathname !== "/change-password") {
        return <Navigate to="/change-password" replace />;
    }

    // Redirect platform_owner to admin
    if (user?.role === "platform_owner") {
        return <Navigate to="/admin" replace />;
    }

    // Redirect portal clients to portal
    if (isPortalClient) {
        return <Navigate to="/portal" replace />;
    }

    return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, isPortalClient, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isAuthenticated && isPortalClient) {
        return <Navigate to="/portal" replace />;
    }

    if (isAuthenticated && user?.role === "platform_owner") {
        return <Navigate to="/admin" replace />;
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Portal Protected Route
const PortalProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, isPortalClient } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!isAuthenticated || !isPortalClient) {
        return <Navigate to="/portal/auth" state={{ from: location }} replace />;
    }

    return children;
};

// Platform Admin Protected Route
const PlatformProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, user } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!isAuthenticated || user?.role !== "platform_owner") {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// App Router
const AppRouter = () => {
    const location = useLocation();

    // Check URL fragment for session_id (OAuth callback)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            {/* Public registration disabled - Tenants created by Super Admin only */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/change-password" element={
                <ProtectedRoute>
                    <ForceChangePasswordPage />
                </ProtectedRoute>
            } />

            {/* Protected Routes */}
            <Route
                element={
                    <ProtectedRoute>
                        <DashboardLayout />
                    </ProtectedRoute>
                }
            >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/clients" element={<FeatureGate feature="CRM"><ClientsPage /></FeatureGate>} />
                <Route path="/clients/:clientId" element={<FeatureGate feature="CRM"><ClientDetailPage /></FeatureGate>} />
                <Route path="/projects" element={<FeatureGate feature="Projets"><ProjectsPage /></FeatureGate>} />
                <Route path="/projects/:projectId" element={<FeatureGate feature="Projets"><ProjectDetailPage /></FeatureGate>} />
                <Route path="/quotes" element={<FeatureGate features={["Factures", "Finances"]}><QuotesPage /></FeatureGate>} />
                <Route path="/invoices" element={<FeatureGate features={["Factures", "Finances"]}><InvoicesPage /></FeatureGate>} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/time-tracking" element={<TimeTrackingPage />} />
                <Route path="/calendar" element={<FeatureGate feature="Calendrier"><CalendarPage /></FeatureGate>} />
                <Route path="/ai-assistant" element={<FeatureGate feature="IA"><AIAssistantPage /></FeatureGate>} />
                <Route path="/documents" element={<FeatureGate feature="Documents"><DocumentsPage /></FeatureGate>} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/billing" element={<BillingSettingsPage />} />
                <Route path="/settings/quotes" element={<QuoteSettingsPage />} />
                <Route path="/settings/subscription" element={<SubscriptionPage />} />
                <Route path="/settings/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/upgrade" element={<UpgradePage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/messaging" element={<MessagingPage />} />
            </Route>

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Portal Routes */}
            <Route path="/portal/auth" element={<PortalAuthPage />} />
            <Route
                element={
                    <PortalProtectedRoute>
                        <PortalLayout />
                    </PortalProtectedRoute>
                }
            >
                <Route path="/portal" element={<PortalDashboardPage />} />
                <Route path="/portal/projects" element={<PortalProjectsPage />} />
                <Route path="/portal/projects/:projectId" element={<PortalProjectDetailPage />} />
                <Route path="/portal/quotes" element={<PortalQuotesPage />} />
                <Route path="/portal/invoices" element={<PortalInvoicesPage />} />
            </Route>

            {/* Platform Admin Routes */}
            <Route
                element={
                    <PlatformProtectedRoute>
                        <SuperAdminLayout />
                    </PlatformProtectedRoute>
                }
            >
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/tenants" element={<AdminTenantsPage />} />
                <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
                <Route path="/admin/plans" element={<AdminPlansPage />} />
                <Route path="/admin/audit" element={<AdminAuditPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <PlanProvider>
                    <NotificationProvider>
                        <OfflineProvider>
                            <BrowserRouter>
                                <AppRouter />
                                <Toaster position="top-right" richColors />
                            </BrowserRouter>
                        </OfflineProvider>
                    </NotificationProvider>
                </PlanProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
